import {CREATE, GET_LIST, showNotification, UPDATE} from 'react-admin';
import {call, put, select, takeLatest} from 'redux-saga/effects';
import {push} from 'react-router-redux';
import {reset} from 'redux-form';
import {
    DRIP_CAMPAIGN_WIZARD_INITIALIZE,
    DRIP_CAMPAIGN_WIZARD_SAVE,
    DRIP_ENROLLMENT_FETCH
} from "../actions/actionTypes";
import {
    dripCampaignWizardFetchMessages,
    dripCampaignWizardSetMessages,
    dripCampaignWizardFetchGroups,
    dripCampaignWizardSetGroups,
    dripCampaignWizardFetchSenders,
    dripCampaignWizardSetSenders,
    dripCampaignWizardSaveSuccess,
    dripCampaignWizardSaveError,
    dripEnrollmentFetched
} from '../actions/dripCampaignActions';
import * as DripCampaignSelector from "../selectors/dripCampaignSelector";
import {dataProvider} from '../components/http/dataProvider';
import i18n from 'i18next';

// Validate the drip campaign data
function validateDripCampaign(dripCampaign) {
    const errors = {};

    if (!dripCampaign.name || dripCampaign.name === '' || dripCampaign.name.length > 150) {
        errors['name'] = 'Invalid name value';
    }

    if (!dripCampaign.subject || dripCampaign.subject === '' || dripCampaign.subject.length > 150) {
        errors['subject'] = 'Invalid subject line value';
    }

    if (!dripCampaign.message) {
        errors['message'] = 'No message selected';
    }

    if (!dripCampaign.targetGroup) {
        errors['targetGroup'] = 'No target group selected';
    }

    if (!dripCampaign.sender) {
        errors['sender'] = 'No sender selected';
    }

    if (!dripCampaign.triggerType) {
        errors['triggerType'] = 'No trigger type selected';
    }

    const delayHours = parseInt(dripCampaign.delayInHours);
    if (isNaN(delayHours) || delayHours < 0 || delayHours > 8760) { // Max 1 year
        errors['delayInHours'] = 'Invalid delay hours (must be 0-8760)';
    }

    return errors;
}

function* initializeDripCampaignWizard() {
    try {
        // Fetch messages (published campaigns only)
        yield put(dripCampaignWizardFetchMessages());
        const messagesResponse = yield call(dataProvider, GET_LIST, 'messages', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name.value', order: 'ASC' },
            filter: { 'publishState.state': 'published' }
        });
        yield put(dripCampaignWizardSetMessages(messagesResponse.data));

        // Fetch groups
        yield put(dripCampaignWizardFetchGroups());
        const groupsResponse = yield call(dataProvider, GET_LIST, 'groups', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name.value', order: 'ASC' },
            filter: {}
        });
        yield put(dripCampaignWizardSetGroups(groupsResponse.data));

        // Fetch senders
        yield put(dripCampaignWizardFetchSenders());
        const sendersResponse = yield call(dataProvider, GET_LIST, 'campaign/senders', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name.value', order: 'ASC' },
            filter: {}
        });
        yield put(dripCampaignWizardSetSenders(sendersResponse.data));

    } catch (error) {
        console.error('Error initializing drip campaign wizard:', error);
        yield put(showNotification(i18n.t('Error loading data'), 'warning'));
    }
}

function* saveDripCampaign({meta}) {
    try {
        const dripCampaign = yield select(DripCampaignSelector.dripCampaignSelectCampaign);

        // Prepare data for API (decorator will handle IRI transformation)
        const campaignData = {
            name: dripCampaign.name,
            subject: dripCampaign.subject,
            message: dripCampaign.message,
            targetGroup: dripCampaign.targetGroup,
            sender: dripCampaign.sender,  // Include sender in API payload
            triggerType: dripCampaign.triggerType,
            delayInHours: parseInt(dripCampaign.delayInHours),
            isActive: dripCampaign.isActive,
            retakeSnapshot: dripCampaign.retakeSnapshot || false
        };

        // Validate the data before saving
        const errors = validateDripCampaign(campaignData);

        if (Object.keys(errors).length > 0) {
            yield put(dripCampaignWizardSaveError(errors));

            let message = '';
            Object.keys(errors).map((key) => message = message + errors[key] + '. ');
            yield put(showNotification(message, 'warning'));
            return;
        }

        let response;
        if (dripCampaign.id) {
            // Update existing campaign
            response = yield call(dataProvider, UPDATE, 'drip-campaigns', {
                id: dripCampaign.id,
                data: campaignData,
                previousData: dripCampaign
            });
        } else {
            // Create new campaign
            response = yield call(dataProvider, CREATE, 'drip-campaigns', {
                data: campaignData
            });
        }

        // Check if response exists and has data
        if (response && response.data) {
            yield put(dripCampaignWizardSaveSuccess(response.data));
            yield put(showNotification(
                dripCampaign.id
                    ? i18n.t('Drip campaign updated successfully')
                    : i18n.t('Drip campaign created successfully')
            ));

            // Reset the redux-form after successful create (not update)
            // This ensures the form is cleared for creating multiple campaigns sequentially
            if (!dripCampaign.id) {
                yield put(reset('record-form'));
            }

            // Handle redirect
            if (meta && meta.redirectTo === 'edit') {
                yield put(push(`/drip-campaigns/${response.data.id}`));
            } else if (meta && meta.redirectTo === 'show') {
                yield put(push(`/drip-campaigns/${response.data.id}/show`));
            } else if (meta && meta.redirectTo === 'list') {
                yield put(push('/drip-campaigns'));
            }
        } else {
            throw new Error('No response data received');
        }

    } catch (error) {
        console.error('Error saving drip campaign:', error);
        yield put(dripCampaignWizardSaveError(error));

        let errorMessage = i18n.t('Something went wrong: %{message}', {message: error.message || 'Unknown error'});

        // Handle different error response formats
        if (error.body && error.body.violations) {
            // Handle API validation errors
            const violations = error.body.violations;
            if (violations.length > 0) {
                errorMessage = violations.map(v => v.message).join(', ');
            }
        } else if (error.status && error.status >= 400) {
            // Handle HTTP errors
            errorMessage = i18n.t('Server error: %{status}', {status: error.status});
        } else if (error.response && error.response.data) {
            // Handle axios-style responses
            errorMessage = error.response.data.message || errorMessage;
        }

        yield put(showNotification(errorMessage, 'warning'));
    }
}

function* fetchDripEnrollments({payload: campaignId}) {
    try {
        const response = yield call(dataProvider, GET_LIST, 'drip-enrollments', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'createdAt', order: 'DESC' },
            filter: { dripCampaignId: campaignId }
        });
        yield put(dripEnrollmentFetched(response.data));
    } catch (error) {
        console.error('Error fetching drip enrollments:', error);
        yield put(showNotification(i18n.t('Error loading enrollments'), 'warning'));
    }
}

export default function* dripCampaignSaga() {
    yield takeLatest(DRIP_CAMPAIGN_WIZARD_INITIALIZE, initializeDripCampaignWizard);
    yield takeLatest(DRIP_CAMPAIGN_WIZARD_SAVE, saveDripCampaign);
    yield takeLatest(DRIP_ENROLLMENT_FETCH, fetchDripEnrollments);
}